import type { PersonaAvatarProfile } from '@repo/contracts';
import { Button } from '@repo/ui/button';
import { Input } from '@repo/ui/input';

import { AlertIcon, CheckIcon, MailIcon } from '../lib/icons';
import { PersonaAvatar } from '../persona/persona-avatar';
import { AuthLayout, SHOWCASE_ASSETS, ShowcasePanel } from './auth-layout';
import { ExtensionSignedIn } from './extension-signed-in';
import { GoogleIcon } from './google-icon';
import { useSignInFlow } from './use-sign-in-flow';

/**
 * Three panel members reacting to "your product" on the showcase side.
 *
 * Real portraits from the same DiceBear style the panel uses (via
 * PersonaAvatar), with the appearance fixed here rather than seeded, so the
 * faces match the people the lines belong to. `place` and `tilt` put each card
 * where the design has it; `delay` staggers the drift so they never bob in
 * step, which is what would make the motion read as motion.
 */
const CARDS: {
  delay: string;
  line: string;
  name: string;
  place: string;
  profile: PersonaAvatarProfile;
  role: string;
  tilt: string;
}[] = [
  {
    delay: '[animation-delay:-1s]',
    line: 'When does it arrive?',
    name: 'Hana',
    place: 'top-[43%] right-[5%]',
    profile: {
      clothes: 'rounded',
      clothingColor: 'f55d81',
      eyes: 'happy',
      hair: 'bobBangs',
      hairColor: 'e15c66',
      mouth: 'smile',
      nose: 'smallRound',
      skinColor: 'e5a07e',
    },
    role: 'Gift buyer',
    tilt: 'rotate-6',
  },
  {
    delay: '[animation-delay:-4s]',
    line: 'Those two hero buttons look the same to me.',
    name: 'Arjun',
    place: 'top-[52%] left-[4%]',
    profile: {
      clothes: 'squared',
      clothingColor: '456dff',
      eyes: 'open',
      facialHair: 'beardMustache',
      hair: 'shortCombover',
      hairColor: '362c47',
      mouth: 'smirk',
      nose: 'mediumRound',
      skinColor: 'b16a5b',
    },
    role: 'Power user',
    tilt: '-rotate-3',
  },
  {
    delay: '[animation-delay:-6s]',
    line: 'I still can’t find the price anywhere.',
    name: 'Kofi',
    place: 'top-[70%] right-[6%]',
    profile: {
      clothes: 'checkered',
      clothingColor: 'f3b63a',
      eyes: 'glasses',
      facialHair: 'goatee',
      hair: 'fade',
      hairColor: '362c47',
      mouth: 'frown',
      nose: 'wrinkles',
      skinColor: '623d36',
    },
    role: 'First-time buyer',
    tilt: '-rotate-6',
  },
];

/**
 * "/login" — sign-in and sign-up in one, since they are one act here: a first
 * Google sign-in or magic link is what creates the account, and onboarding
 * finishes it. The form on the left; on the right a room in miniature — a
 * product's skeleton with panel members drifting around it, saying the kind
 * of thing a room says.
 *
 * New users arrive from floto.ai with a product to test (`/?url=…&utm_*`):
 * ProtectedRoute sends them here with that whole path as `redirect`, the
 * line under the headline names the product, and after sign-in (and
 * onboarding, for a new user) the home route starts a session for it. The
 * flow itself — both Firebase calls, the extension handoff, where to go
 * next — lives in `useSignInFlow`.
 *
 * The showcase is decorative and hidden from assistive tech; below `lg` it is
 * dropped altogether so the form keeps the whole screen.
 */
export function LoginPage() {
  const {
    email,
    emailError,
    error,
    existingAccount,
    isExtensionFlow,
    isSubmitting,
    magicLinkSent,
    magicLinkSubmitting,
    onEmailChange,
    onSendMagicLink,
    onSignIn,
    signedIn,
    target,
  } = useSignInFlow();

  if (signedIn) return isExtensionFlow ? <ExtensionSignedIn /> : null;

  return (
    <AuthLayout showcase={<Showcase />}>
      <div className="flex flex-1 justify-center pb-[24px]">
        {/* The gap under the logo is the slack: the full design gap on a
              tall screen, shrinking (to 24px) before the page would scroll.
              635px is everything else in this column — header, form and
              padding — so the form ends at the bottom of the window. */}
        <div className="mt-16 w-full max-w-sm duration-500 lg:mt-[clamp(24px,calc(100svh-635px),142px)] animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none">
          <h1 className="!my-0 !text-auth-title !font-bold !text-text-primary">
            Stop building
            <br />
            without{' '}
            <span className="inline-block rounded-button bg-sunny px-2 pb-1">
              feedback.
            </span>
          </h1>
          <p className="!mt-[13px] text-heading-xs text-text-control">
            {target ? (
              <>
                Sign in to get feedback on{' '}
                <span className="font-semibold text-text-primary">
                  {new URL(target).host}
                </span>
                .
              </>
            ) : (
              'Sign In'
            )}
          </p>

          {magicLinkSent ? (
            <div
              className="mt-[19px] flex items-start gap-2.5 rounded-button bg-highlight-soft px-4 py-3.5 text-ui-strong text-text-primary"
              role="status"
            >
              <CheckIcon className="mt-0.5 size-4 shrink-0" />
              <span>
                Check <strong>{email}</strong>{' '}
                {existingAccount === true
                  ? 'for your sign-in link.'
                  : existingAccount === false
                    ? 'for a link to finish creating your account.'
                    : 'for a link to sign in.'}
              </span>
            </div>
          ) : (
            <>
              <Button
                className="mt-[19px] h-12 w-full cursor-pointer justify-center gap-3 rounded-button border border-hairline-alt bg-surface text-ui-lg font-semibold text-text-primary shadow-soft hover:bg-surface-hover active:scale-[0.98]"
                disabled={isSubmitting}
                onClick={onSignIn}
                type="button"
              >
                <GoogleIcon className="size-4" />
                {isSubmitting ? 'Signing in…' : 'Continue with Google'}
              </Button>

              <div className="my-[22px] flex items-center gap-3 text-tag font-medium tracking-wider text-text-muted uppercase">
                <span className="h-px flex-1 bg-hairline" />
                or
                <span className="h-px flex-1 bg-hairline" />
              </div>

              {/* noValidate: the browser's own bubble would beat the inline
                  message below to it, and says less. */}
              <form
                noValidate
                onSubmit={(formEvent) => {
                  formEvent.preventDefault();
                  onSendMagicLink();
                }}
              >
                <label
                  className="block text-ui-strong font-semibold text-text-primary"
                  htmlFor="signup-email"
                >
                  Enter your mail address
                </label>
                <div className="relative mt-2">
                  <MailIcon
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-text-muted"
                  />
                  <Input
                    aria-describedby={
                      emailError ? 'signup-email-error' : undefined
                    }
                    aria-invalid={emailError ? true : undefined}
                    autoComplete="email"
                    className={`h-12 rounded-button pl-10 text-ui-lg ${
                      emailError
                        ? 'border-critical focus-visible:border-critical'
                        : 'border-hairline-alt'
                    }`}
                    disabled={magicLinkSubmitting}
                    id="signup-email"
                    onChange={(changeEvent) =>
                      onEmailChange(changeEvent.target.value)
                    }
                    placeholder="you@company.com"
                    required
                    type="email"
                    value={email}
                  />
                </div>
                {emailError ? (
                  <p
                    className="!mt-2 flex items-center gap-1.5 text-caption font-medium text-critical"
                    id="signup-email-error"
                    role="alert"
                  >
                    <AlertIcon
                      aria-hidden="true"
                      className="size-3.5 shrink-0"
                    />
                    {emailError}
                  </p>
                ) : null}
                <Button
                  className="mt-[22px] h-12 w-full cursor-pointer justify-center rounded-button bg-brand text-ui-lg font-semibold text-white hover:bg-brand-alt active:scale-[0.98]"
                  disabled={magicLinkSubmitting}
                  type="submit"
                >
                  {magicLinkSubmitting ? 'Sending…' : 'Continue with email'}
                </Button>
              </form>
            </>
          )}

          {error ? (
            <div
              className="mt-4 rounded-button bg-tag-critical px-3 py-2 text-ui text-text-primary"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <p className="!mt-[24px] text-center text-caption leading-relaxed text-text-muted">
            We&rsquo;ll email you a sign-in link, no password needed. By
            continuing you agree to the{' '}
            <LegalLink href="https://floto.ai/terms-of-service">
              Terms
            </LegalLink>{' '}
            and{' '}
            <LegalLink href="https://floto.ai/privacy-policy">
              Privacy Policy
            </LegalLink>
            .
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}

function Showcase() {
  return (
    <ShowcasePanel title="Meet your users before they meet your product.">
      <img
        alt=""
        className="absolute bottom-0 left-0 -z-10 w-28 xl:w-32"
        src={`${SHOWCASE_ASSETS}/yellow-graphic.svg`}
      />

      {CARDS.map((card) => (
        <div className={`absolute ${card.place}`} key={card.name}>
          {/* Drift and tilt on separate elements: both are transforms, and on
              one element the animation would overwrite the rotation. */}
          <div
            className={`animate-drift motion-reduce:animate-none ${card.delay}`}
          >
            <div
              className={`w-56 rounded-card bg-surface p-4 shadow-floating xl:w-60 ${card.tilt}`}
            >
              <div className="flex items-center gap-2.5">
                <PersonaAvatar
                  persona={{
                    accent: 0,
                    avatarProfile: card.profile,
                    id: `signup-${card.name}`,
                    name: card.name,
                  }}
                  size="md"
                />
                <div className="leading-tight">
                  <p className="text-ui-strong font-bold text-text-primary">
                    {card.name}
                  </p>
                  <p className="text-tag text-text-secondary">{card.role}</p>
                </div>
              </div>
              <p className="!mt-3 text-ui-lg leading-snug font-semibold text-text-primary">
                {card.line}
              </p>
            </div>
          </div>
        </div>
      ))}
    </ShowcasePanel>
  );
}

/** The legal pages live on the marketing site; a new tab keeps the form where
 *  it was. */
function LegalLink({ children, href }: { children: string; href: string }) {
  return (
    <a
      className="text-room-violet underline underline-offset-2 hover:text-text-primary"
      href={href}
      rel="noreferrer"
      target="_blank"
    >
      {children}
    </a>
  );
}
